/**
 * antd 全局主题（`config/config.ts` → `antd` 运行时）：深浅两档 `ThemeConfig`，与 Pro 侧栏无关。
 * 布局/侧栏/页面容器背景见同目录 `proLayoutThemeByMode.ts`；静态 Pro 默认值见 `defaultSettings.ts`。
 */
import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';

/**
 * 浅色 / 深色 antd 补丁：`token` 两档键对齐；Menu 在浅色档只配 `item*`（`ant-menu-light`），深色档另配 `darkItem*`（`ant-menu-dark`）。
 * 根三色：浅色 `colorBgLayout` / `colorBgContainer` / `colorPrimary` 均为 `#ffffff`；深色均为 `#171717`。
 */

/**
 * 侧栏菜单项/子菜单标题圆角：与 `src/global.less` 中 `@video-ui-menu-item-radius` 保持一致。
 * 说明：`components.Menu.itemBorderRadius` 对**浮层三级**（走 `subMenuItemBorderRadius`）有效；一级、二级在 Pro `SiderMenu/style/menu.js`
 * 里还会被 `borderRadius: token.borderRadiusLG` 等规则覆盖，故侧栏必须在 `global.less` 再写一层（见该文件注释）。
 */
export const MENU_ITEM_BORDER_RADIUS = '0.725rem';

/** 侧栏菜单悬停/按下背景（两档一致；深色 :active 另见 `src/global.less` 覆盖 antd 默认 transparent） */
export const MENU_ITEM_HOVER_BG_LIGHT = '#0000000a';
export const MENU_ITEM_HOVER_BG_DARK = '#262626';

/**
 * Segmented 分段控件（AssetSelector Tab、页内切换等）统一配色。
 * 使用方式：`<Segmented />` 即可，勿在业务 less 里写 itemSelected*。
 */
export const SEGMENTED_THEME_LIGHT = {
  trackBg: 'hsl(0 0% 9% / .08)',
  trackPadding: 2,
  itemColor: 'hsl(0 0% 63.9%)',
  itemHoverColor: '#18181b',
  itemHoverBg: 'transparent',
  itemActiveBg: MENU_ITEM_HOVER_BG_LIGHT,
  itemSelectedBg: '#ffffff',
  itemSelectedColor: '#18181b',
} as const;

export const SEGMENTED_THEME_DARK = {
  trackBg: 'rgba(255, 255, 255, 0.08)',
  trackPadding: 2,
  itemColor: 'hsl(0 0% 63.9%)',
  itemHoverColor: '#f4f4f5',
  itemHoverBg: 'transparent',
  itemActiveBg: MENU_ITEM_HOVER_BG_DARK,
  itemSelectedBg: MENU_ITEM_HOVER_BG_DARK,
  itemSelectedColor: '#f4f4f5',
} as const;

/**
 * 全局默认 Button 圆角（与主题在线编辑器里写在 `components.Button` 下的方式一致）。
 * antd 允许在 `components.Button` 中覆盖**仅作用于 Button** 的 Alias Token（见 ConfigProvider 文档「覆盖该组件消费的 Alias Token」）。
 * - 默认 / 中号：用 `borderRadius`
 * - `size="small"`：样式里会改用 `borderRadiusSM`
 * - `size="large"`：样式里会改用 `borderRadiusLG`
 * 若某处需要不同圆角，在页面用 `style={{ borderRadius: … }}` 或局部再包一层 `ConfigProvider` 覆盖即可。
 * 注意：`shape="round"` 会用胶囊圆角（约等于高度的一半），不会按这里的数值显示。
 */
/** 深色模式下的 antd 主题补丁（`token` 与浅色档对齐；`Menu` 含 `darkItem*`）。 */
export const darkModeAntdTheme: ThemeConfig = {
  token: {
    colorBgLayout: '#171717',
    colorBgContainer: '#171717',
    /**
     * 全局主色
     */
    colorPrimary: 'rgb(250, 250, 250)',
    /**
     * 与浅色对称：主色与页面同深时，激活项用浅底托深字（浅色档则为深底托浅字）。
     */
    colorPrimaryBg: '#262626',
    controlItemBgActive: '#262626',
    controlItemBgActiveHover: '#262626',

    colorLink: 'hsl(0 0% 63.9%)',
    colorLinkHover: 'color-mix(in srgb, var(--ant-color-link) 72%, white)',
    colorLinkActive: 'color-mix(in srgb, var(--ant-color-link) 58%, white)',
  },
  components: {
    Menu: {
      /** 菜单项、子菜单标题圆角；侧栏一级/二级需配合 global.less */
      itemBorderRadius: MENU_ITEM_BORDER_RADIUS as unknown as number,
      subMenuItemBorderRadius: MENU_ITEM_BORDER_RADIUS as unknown as number,
      /** 行高与上下 padding 的细调见 `global.less`（antd 将行高与 item 高度绑在一起） */
      itemHeight: '1.25rem',
      itemMarginBlock: 0,
      groupTitleFontSize: 12,
      groupTitleLineHeight: '1rem',
      /** 深色侧栏：普通项文字 */
      darkItemColor: '#f4f4f5',
      darkItemHoverColor: '#f4f4f5',
      darkItemSelectedColor: '#f4f4f5',
      subMenuItemSelectedColor: '#f4f4f5',
      /** 分组标题（antd Menu.ItemGroup；Pro 分组见 global.less） */
      darkGroupTitleColor: '#f4f4f5b3',
      darkItemHoverBg: MENU_ITEM_HOVER_BG_DARK,
      darkItemSelectedBg: MENU_ITEM_HOVER_BG_DARK,
    },
    Button: {
      fontWeight: 500,
      borderRadius: 8,
      borderRadiusSM: 8,
      borderRadiusLG: 8,
      /** primary 按钮悬停颜色 */
      colorPrimaryHover: 'rgb(250, 250, 250, 90%)',
      /** primary 按钮文字颜色 */
      primaryColor: 'hsl(0 0% 9%)',
      /** primary 按钮背景颜色 */
      colorPrimary: 'rgb(250, 250, 250)',

      /** 禁用按钮背景颜色 */
      colorBgContainerDisabled: 'rgb(250, 250, 250, 98%)',
      /** 禁用按钮文字颜色 */
      colorTextDisabled: 'hsl(0 0% 9%)',

      /** link 按钮 */
      colorLink: 'rgb(250, 250, 250)',
      colorLinkHover: 'hsl(var(--primary)/.8)',
      colorLinkActive: 'rgb(250, 250, 250, 90%)',

      /** default 按钮悬停边框颜色 */
      defaultHoverBorderColor: 'rgb(250, 250, 250, 30%)',
    },
    Tag: {
      /**
       * Tag.CheckableTag 选中/悬停使用的 token 来自 alias：
       * - checked bg: colorPrimary
       * - checked hover bg: colorPrimaryHover
       * - active bg: colorPrimaryActive
       * - checked text: colorTextLightSolid
       * - unchecked hover bg: colorFillSecondary（见 tag/style/index.js）
       */
      colorPrimary: 'rgb(250, 250, 250)',
      colorPrimaryHover: 'rgb(250, 250, 250, 90%)',
      colorPrimaryActive: 'rgb(250, 250, 250, 80%)',
      colorTextLightSolid: 'hsl(0 0% 9%)',
      colorFillSecondary: 'rgba(250, 250, 250, 0.08)',

      /** 其它 Tag 主色边框（保留原意） */
      colorPrimaryBorder: 'rgb(250, 250, 250, 90%)',
      colorPrimaryBorderHover: 'rgb(250, 250, 250, 90%)',
    },
    Segmented: {
      ...SEGMENTED_THEME_DARK,
    },
  },
};

/**
 * 浅色模式下的 antd 主题补丁（在 config/config.ts 的 antd 基础配置之上叠加）。
 */
export const lightModeAntdTheme: ThemeConfig = {
  token: {
    colorBgLayout: '#ffffff',
    colorBgContainer: '#ffffff',
    /**
     * 全局主色
     */
    colorPrimary: '#000000e0',
    /**
     * 与浅色对称：主色与页面同深时，激活项用浅底托深字（浅色档则为深底托浅字）。
     */
    colorPrimaryBg: '#0000000a',
    controlItemBgActive: '#0000000a',
    controlItemBgActiveHover: '#0000000a',
    /**
     * 全局 `<a>`：默认与菜单字色一致；悬停/按下在 colorLink 上混黑加深（百分比越小越深）。
     */
    colorLink: 'hsl(0 0% 45.1%)',
    colorLinkHover: 'color-mix(in srgb, var(--ant-color-link) 72%, black)',
    colorLinkActive: 'color-mix(in srgb, var(--ant-color-link) 58%, black)',
  },
  components: {
    Menu: {
      itemBorderRadius: MENU_ITEM_BORDER_RADIUS as unknown as number,
      subMenuItemBorderRadius: MENU_ITEM_BORDER_RADIUS as unknown as number,
      itemHeight: '1.25rem',
      itemMarginBlock: 0,
      groupTitleFontSize: 12,
      groupTitleLineHeight: '1rem',
      /** 浅色侧栏：普通项文字 */
      itemColor: '#18181b',
      itemHoverColor: '#18181b',
      itemSelectedColor: '#18181b',
      subMenuItemSelectedColor: '#18181b',
      groupTitleColor: '#3f3f46b3',
      itemHoverBg: MENU_ITEM_HOVER_BG_LIGHT,
      itemActiveBg: MENU_ITEM_HOVER_BG_LIGHT,
      itemSelectedBg: MENU_ITEM_HOVER_BG_LIGHT,
    },
    Button: {
      fontWeight: 500,
      borderRadius: 8,
      borderRadiusSM: 8,
      borderRadiusLG: 8,
      /** primary 按钮悬停颜色 */
      colorPrimaryHover: 'hsl(0 0% 9% / .9)',
      /** primary 按钮文字颜色 */
      primaryColor: 'hsl(0 0% 98%)',
      /** primary 按钮背景颜色 */
      colorPrimary: 'hsl(0 0% 9%)',

      /** 禁用按钮背景颜色 */
      colorBgContainerDisabled: 'hsl(0 0% 9%)',
      /** 禁用按钮文字颜色 */
      colorTextDisabled: 'hsl(0 0% 98%)',

      /** default 按钮悬停边框颜色 */
      defaultHoverBorderColor: 'hsl(0 0% 9% / .3)',
    },
    Tag: {
      /**
       * Tag.CheckableTag 选中/悬停使用的 token 来自 alias：
       * - checked bg: colorPrimary
       * - checked hover bg: colorPrimaryHover
       * - active bg: colorPrimaryActive
       * - checked text: colorTextLightSolid
       * - unchecked hover bg: colorFillSecondary（见 tag/style/index.js）
       */
      colorPrimary: 'hsl(0 0% 9%)',
      colorPrimaryHover: 'hsl(0 0% 9% / .9)',
      colorPrimaryActive: 'hsl(0 0% 9% / .8)',
      colorTextLightSolid: 'hsl(0 0% 98%)',
      colorFillSecondary: 'hsl(0 0% 9% / .08)',

      /** 其它 Tag 主色边框（保留原意） */
      colorPrimaryBorder: 'hsl(0 0% 9% / .9)',
      colorPrimaryBorderHover: 'hsl(0 0% 99%)',
    },
    Segmented: {
      ...SEGMENTED_THEME_LIGHT,
    },
    DatePicker: {
      colorTextPlaceholder: '#000000e0',
    },
  },
};


export type NavThemeMode = 'light' | 'realDark';

export function getModeThemePatch(nav: NavThemeMode): ThemeConfig {
  return nav === 'realDark' ? darkModeAntdTheme : lightModeAntdTheme;
}

/**
 * 供 ProLayout `settings.colorPrimary` 使用。
 * Pro 会在内部再包一层 antd `ConfigProvider` 并显式传入 `theme.token.colorPrimary`；
 * 仅配置根上 `app.tsx` 的 `antd` 运行时主题时，主色不会走这条路径，容易看起来像「没生效」。
 * 与 {@link getModeThemePatch} 同源，避免两处各写一份。
 */
export function getLayoutColorPrimaryForNav(nav: NavThemeMode): string {
  const p = getModeThemePatch(nav).token?.colorPrimary;
  return typeof p === 'string' ? p : '#1677FF';
}

/**
 * 在保留 config 里全局 token / 其它组件配置的前提下，按深浅色切换算法与对应补丁。
 */
export function mergeAntdThemeForNavMode(
  prev: ThemeConfig | undefined,
  nav: NavThemeMode,
): ThemeConfig {
  const patch = getModeThemePatch(nav);
  return {
    ...prev,
    algorithm:
      nav === 'realDark'
        ? antdTheme.darkAlgorithm
        : antdTheme.defaultAlgorithm,
    token: {
      ...prev?.token,
      ...patch.token,
    },
    components: {
      ...prev?.components,
      ...patch.components,
      Button: {
        ...prev?.components?.Button,
        ...patch.components?.Button,
      },
      Tag: {
        ...prev?.components?.Tag,
        ...patch.components?.Tag,
      },
      Radio: {
        ...prev?.components?.Radio,
        ...patch.components?.Radio,
      },
      Menu: {
        ...prev?.components?.Menu,
        ...patch.components?.Menu,
      },
      Segmented: {
        ...prev?.components?.Segmented,
        ...patch.components?.Segmented,
      },
    },
  };
}
